<script setup lang="ts">
import { computed, ref, watch, watchEffect, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import {
  svgIcon,
  baseAvatar,
  baseInput,
  baseConfirm,
} from "@/common/components";

import { addressList } from "@/common/api/rest/address";
import { contactList } from "@/common/api/rest/contact";
import { companyList } from "@/common/api/rest/company";
import {
  evaluationCompanyShow,
  evaluationCompanyCreate,
  evaluationCompanyPatch,
  evaluationCompanyDelete,
} from "@/common/api/rest/evaluation/company";

import type { CompanyListType, CompanyType } from "@/common/types/company";
import type { EvaluationType } from "@/common/types/evaluation";
import type { AddressType, ContactType } from "@/common/types/common";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EvaluationType | null;
    width?: string;
    showTitle?: boolean;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    showTitle: true,
    isEdit: true,
  },
);

const company = ref<CompanyType | null>(null);
const address = ref<any[]>([]);
const contact = ref<any[]>([]);

const listData = ref<CompanyListType[]>([]);
const selected = ref<CompanyListType | null>(null);
const selectAddress = ref<string | null>(null);
const selectContact = ref<string | null>(null);
const inputSearch = ref("");

const searched = ref(false);
const showAddConfirm = ref(false);
const showDelConfirm = ref(false);

const addressData = computed<AddressType | null>(
  () =>
    address.value.filter(
      (address) => address.address_id === selectAddress.value,
    )[0] ?? null,
);

const contactData = computed<ContactType | null>(
  () =>
    contact.value.filter(
      (address) => address.contact_id === selectContact.value,
    )[0] ?? null,
);

function search() {
  if (!props.data || props.data.company) return;

  searched.value = true;
  companyList(props.data?.account_id, {
    search: inputSearch.value,
    limit: 100,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        listData.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchAddress() {
  if (!props.data || !props.data.company) return;

  searched.value = true;
  addressList(props.data.account_id, props.data.company.company_id, {
    search: inputSearch.value,
    limit: 100,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        address.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchContact() {
  if (!props.data || !props.data.company) return;

  searched.value = true;

  contactList(props.data.account_id, props.data.company.company_id, {
    search: inputSearch.value,
    limit: 100,
  })
    .then((response) => {
      const { success, data } = response;

      if (success) {
        contact.value = data.list;
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchEvaluationCompany() {
  if (!props.data?.company) return;

  evaluationCompanyShow(props.data.account_id, props.data.company.item_id)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        company.value = props.data.company;
        selectAddress.value = data.address_id;
        selectContact.value = data.contact_id;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function editEvaluationCompany(key: string, value: string) {
  if (!company.value) return;

  const payload = {
    address_id: company.value.address_id,
    contact_id: company.value.contact_id,
    ...{ [key]: value },
  };

  evaluationCompanyPatch(
    props.data.account_id,
    props.data.company.item_id,
    payload,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchEvaluationCompany();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addCompany() {
  if (!props.data?.account_id || !props.data?.evaluation_id || !selected.value)
    return;

  evaluationCompanyCreate(
    props.data.account_id,
    props.data.evaluation_id,
    selected.value.company_id,
  )
    .then((response) => {
      const { success, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        fetchEvaluationCompany();
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAddress(id: string) {
  editEvaluationCompany("address_id", id);
}

function changeContact(id: string) {
  editEvaluationCompany("contact_id", id);
}

function add(data: CompanyListType) {
  selected.value = data;
  showAddConfirm.value = true;
}

function addCancel() {
  selected.value = null;
  showAddConfirm.value = false;
}

function addConfirm() {
  if (!props.data) return;

  addCompany();

  searched.value = false;
  inputSearch.value = "";
  addCancel();
}

function del() {
  showDelConfirm.value = true;
}

function delCancel() {
  showDelConfirm.value = false;
}

function delConfirm() {
  if (!props.data || !props.data.company) return;

  evaluationCompanyDelete(props.data.account_id, props.data.company.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        company.value = null;
        update();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });

  delCancel();
}

function update() {
  emit("update-data");
}

onMounted(() => {
  fetchEvaluationCompany();
  fetchAddress();
  fetchContact();
  search();
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <template v-if="showTitle">
      <div class="title">
        {{ t("common.labels.company") }}
      </div>
    </template>

    <div class="content">
      <template v-if="!data?.company && isEdit">
        <div class="search">
          <base-input
            v-model="inputSearch"
            background="#fff"
            color="#000"
            :placeholder="t('common.labels.search')"
            :disabled="!!data?.company?.company_id"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon icon="search" size="1.75rem" @click="search" />
              </div>
            </template>
          </base-input>
        </div>

        <template v-if="searched">
          <div class="list">
            <template v-for="(item, index) in listData" :key="index">
              <div class="item" @click="add(item)">
                <base-avatar :url="item?.avatar?.small" size="1.5rem" />

                <div class="company">
                  <div class="name">
                    {{ item.name }}
                  </div>
                  <div class="document">
                    {{ item.document_name }} - {{ item.document_number }}
                  </div>
                </div>
              </div>
            </template>

            <template v-if="listData.length === 0">
              <div class="empty">
                {{ t("common.texts.no_results") }}
              </div>
            </template>
          </div>
        </template>
      </template>

      <template v-if="!data?.company && !isEdit">
        <div class="empty">
          {{ t("common.texts.no_results") }}
        </div>
      </template>

      <template v-if="company">
        <div class="company-data">
          <div class="header">
            <base-avatar :url="company?.avatar?.small" size="3rem" />

            <div class="company">
              <div class="name">
                {{ company.name }}
              </div>
              <div class="document">
                {{ company.document_name }} -
                {{ company.document_number }}
              </div>
            </div>

            <template v-if="isEdit">
              <div class="action">
                <button
                  type="button"
                  class="btn btn-none btn-sm remove"
                  @click="del()"
                >
                  <svg-icon icon="trash" size="1.75rem" />
                </button>
              </div>
            </template>
          </div>

          <div class="addresses">
            <template v-if="isEdit">
              <div class="label border">
                <strong>
                  {{ t("common.labels.addresses").toUpperCase() }}
                </strong>
              </div>
              <template v-for="(item, index) in address" :key="index">
                <div class="item">
                  <div class="checkbox">
                    <input
                      type="radio"
                      v-model="selectAddress"
                      :value="item.address_id"
                      @change="changeAddress(item.address_id)"
                    />
                  </div>
                  <div class="infos">
                    <div class="name">
                      {{ item.name }}
                    </div>
                    <div class="address">
                      {{ item.address }} - {{ item.district }} -
                      {{ item.city }} - {{ item.state }} - {{ item.country }} -
                      {{ item.cep }}
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <template v-else>
              <div class="label border">
                <strong>
                  {{ t("common.labels.address").toUpperCase() }}
                </strong>
              </div>
              <div class="item">
                <template v-if="addressData">
                  <div class="infos">
                    <div class="name">
                      {{ addressData.name }}
                    </div>
                    <div class="address">
                      {{ addressData.address }} - {{ addressData.district }} -
                      {{ addressData.city }} - {{ addressData.state }} -
                      {{ addressData.country }} - {{ addressData.cep }}
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>

          <div class="contacts">
            <template v-if="isEdit">
              <div class="label border">
                <strong>
                  {{ t("common.labels.contacts").toUpperCase() }}
                </strong>
              </div>
              <template v-for="(item, index) in contact" :key="index">
                <div class="item">
                  <div class="checkbox">
                    <input
                      type="radio"
                      v-model="selectContact"
                      :value="item.contact_id"
                      @change="changeContact(item.contact_id)"
                    />
                  </div>

                  <div class="infos">
                    <div class="name">
                      {{ item.name }}
                    </div>
                    <div class="values">
                      <label> {{ t(`common.labels.${item.type}`) }}: </label>
                      <span>
                        {{ item.contact }}
                      </span>
                    </div>
                    <div class="values">
                      <label> {{ t(`common.labels.description`) }}: </label>
                      <span v-html="item.description" />
                    </div>
                  </div>
                </div>
              </template>
            </template>
            <template v-else>
              <div class="label border">
                <strong>
                  {{ t("common.labels.contact").toUpperCase() }}
                </strong>
              </div>
              <div class="item">
                <template v-if="contactData">
                  <div class="infos">
                    <div class="name">
                      {{ contactData.name }}
                    </div>
                    <div class="values">
                      <label>
                        {{ t(`common.labels.${contactData.type}`) }}:
                      </label>
                      <span>
                        {{ contactData.contact }}
                      </span>
                    </div>
                    <div class="values">
                      <label> {{ t(`common.labels.description`) }}: </label>
                      <span v-html="contactData.description" />
                    </div>
                  </div>
                </template>
              </div>
            </template>
          </div>

          <div class="descriptions">
            <div class="label border">
              <strong>
                {{ t("common.labels.description").toUpperCase() }}
              </strong>
            </div>
            <div class="value" v-html="company.description" />
          </div>
        </div>
      </template>
    </div>

    <base-confirm
      :show="showAddConfirm"
      max-width="500px"
      :title="t('evaluation.modal.titles.add_company').toUpperCase()"
      :message="
        t('evaluation.modal.messages.add_company', {
          concat: selected?.name,
        })
      "
      @cancel="addCancel"
      @confirm="addConfirm"
    />

    <base-confirm
      :show="showDelConfirm"
      max-width="500px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.company'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: t('common.labels.company'),
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border);
    padding-block-end: 0.5rem;
  }

  .search {
    width: 100%;

    .icon {
      cursor: pointer;
      color: var(--text-off);

      &:hover {
        color: var(--primary);
      }
    }
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-block: 1rem;
    padding-inline: 0.5rem;

    .item {
      cursor: pointer;
      user-select: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.7rem;
      padding-block: 0.75rem;
      border-block-end: 1px solid var(--border);

      .company {
        flex: 3;

        .name {
          font-weight: bold;
        }

        .document {
          font-size: 0.875rem;
        }
      }
    }
  }

  .border {
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.4rem;
  }

  .company-data {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    margin-block: 1rem;

    .header {
      display: flex;
      flex-direction: row;
      gap: 1rem;
      margin-block-end: 1rem;

      .company {
        flex: 2;

        .name {
          font-size: 1.15rem;
          font-weight: bold;
        }

        .document {
          font-size: 0.875rem;
        }
      }

      .action {
        .remove {
          &:hover {
            color: var(--red);
          }
        }
      }
    }

    .addresses {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .item {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        padding-inline: 1rem;

        .infos {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
      }
    }

    .descriptions {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .label {
        font-weight: bold;
      }

      .value {
        font-size: 0.9rem;
        text-align: justify;
        padding-inline: 1rem;
      }
    }

    .contacts {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .item {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        padding-inline: 1rem;

        .infos {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .name {
          font-weight: bold;
        }

        .values {
          span {
            font-weight: bold;
          }
        }
      }
    }
  }
}
</style>
